import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../App';
import collegeConfig from '../../config/collegeConfig';
import ReattemptRequestModal from '../shared/ReattemptRequestModal';
import EntryDetailsModal from '../shared/EntryDetailsModal';
import MonthPicker from '../shared/MonthPicker';
import { getStatusBadge, formatDate, renderEntryData } from '../shared/logbookDisplayUtils';
import {
  FaUserTie,
  FaSignOutAlt,
  FaSpinner,
  FaHistory,
  FaUserFriends,
  FaRedo,
  FaSearch,
  FaUser,
  FaEye,
  FaBookMedical,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';

const REATTEMPT_ELIGIBLE_STATUSES = ['submitted', 'under_review', 'approved'];

export default function DeanDashboard() {
  const { user, logout } = useAuth();
  const api = collegeConfig.api.baseURL;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [departments, setDepartments] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batch, setBatch] = useState('');
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modules, setModules] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reattemptTarget, setReattemptTarget] = useState(null);
  const [detailsEntryId, setDetailsEntryId] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  // Expanded by default — track only the ones explicitly collapsed
  const toggleCard = (entryId) => {
    setExpandedCards(prev => ({
      ...prev,
      [entryId]: prev[entryId] === false ? true : false
    }));
  };

  useEffect(() => {
    fetch(`${api}/monitor/departments`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setDepartments(d.departments || []); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBatch('');
    setBatches([]);
    setModules([]);
    setEntries([]);
    setHasLoaded(false);
    if (!courseId) return;

    setBatchLoading(true);
    fetch(`${api}/monitor/academic-batches?courseId=${courseId}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setBatches(d.batches || []); })
      .catch(() => {})
      .finally(() => setBatchLoading(false));

    fetch(`${api}/courses/${courseId}`, { headers })
      .then(r => r.json())
      .then(d => { if (d.success) setModules(d.course?.modules || []); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const getModuleName = (moduleId) => modules.find(m => m.moduleId === moduleId)?.name || 'Unknown Module';

  const loadEntries = useCallback(async () => {
    if (!courseId || !batch) return;
    setLoading(true);
    setError('');
    try {
      let sd = startDate, ed = endDate;
      if (!sd && !ed) {
        // Build the date string from local Y/M/D components — toISOString() would convert
        // to UTC first and shift the date back a day in timezones ahead of UTC (e.g. IST).
        const toLocalDateStr = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const [y, m] = month.split('-').map(Number);
        sd = toLocalDateStr(new Date(y, m - 1, 1));
        ed = toLocalDateStr(new Date(y, m, 0));
      }
      const params = new URLSearchParams({ startDate: sd, endDate: ed });

      const [entriesRes, studentsRes] = await Promise.all([
        fetch(`${api}/logbook/entries/course/${courseId}?${params}`, { headers }),
        fetch(`${api}/students/by-course/${courseId}`, { headers })
      ]);
      const entriesData = await entriesRes.json();
      const studentsData = await studentsRes.json();

      if (!entriesData.success) throw new Error(entriesData.message || 'Failed to load entries');

      const batchStudentIds = new Set(
        (studentsData.students || [])
          .filter(s => s.assignedCourses?.some(
            ac => (ac.courseId?.toString ? ac.courseId.toString() : ac.courseId) === courseId && ac.batch === batch
          ))
          .map(s => s._id)
      );

      const filtered = (entriesData.entries || []).filter(e => {
        const sid = e.studentId?._id || e.studentId;
        return batchStudentIds.has(sid);
      });

      setEntries(filtered);
      setHasLoaded(true);
    } catch (err) {
      setError(err.message || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, batch, month, startDate, endDate, api]);

  return (
    <div className="view-logbooks-container">
      <div className="logbooks-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2><FaUserTie style={{ marginRight: 8 }} />Dean Dashboard</h2>
          <p>
            {user?.profile?.firstName} {user?.profile?.lastName} — browse entries across any department and request reattempts
          </p>
        </div>
        <button className="btn-cancel" onClick={logout}>
          <FaSignOutAlt style={{ marginRight: 6 }} /> Logout
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="logbooks-filters">
        <div className="filter-row">
          <div className="filter-group">
            <div className="filter-item">
              <label>Department</label>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">— Select Department —</option>
                {departments.map(d => (
                  <option key={d.courseId} value={d.courseId}>{d.courseName}</option>
                ))}
              </select>
            </div>

            <div className="filter-item">
              <label>Batch</label>
              <select
                value={batch}
                onChange={(e) => setBatch(e.target.value)}
                disabled={!courseId || batchLoading}
              >
                <option value="">{batchLoading ? 'Loading…' : courseId ? '— Select Batch —' : '— Select department first —'}</option>
                {batches.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <MonthPicker value={month} onChange={(m) => { setMonth(m); setStartDate(''); setEndDate(''); }} />

            <div className="filter-item">
              <label>Start Date (optional)</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>

            <div className="filter-item">
              <label>End Date (optional)</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        <button
          className="bulk-verify-btn"
          onClick={loadEntries}
          disabled={!courseId || !batch || loading}
        >
          {loading ? <FaSpinner className="spinner" /> : <><FaSearch /> Load Entries</>}
        </button>
      </div>

      <div className="logbooks-content">
        {loading && (
          <div className="logbooks-loading">
            <FaSpinner className="spinner" />
            <p>Loading entries...</p>
          </div>
        )}

        {!loading && hasLoaded && entries.length === 0 && (
          <div className="no-logbooks">
            <FaBookMedical className="no-logbooks-icon" />
            <h3>No entries found</h3>
            <p>Try a different department, batch, or period</p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="logbooks-list">
            <div className="month-entries">
              {entries.map(entry => {
                const status = getStatusBadge(entry.status);
                const eligible = REATTEMPT_ELIGIBLE_STATUSES.includes(entry.status);
                const isExpanded = expandedCards[entry._id] !== false;
                return (
                  <div key={entry._id} className={`logbook-card ${isExpanded ? 'expanded' : ''}`}>
                    <div className="logbook-header">
                      <div className="student-info">
                        <div className="student-avatar">
                          <FaUser />
                        </div>
                        <div className="student-details">
                          <h4>
                            {entry.studentId?.profile?.firstName} {entry.studentId?.profile?.lastName}
                          </h4>
                          <span className="student-email">{entry.studentId?.email}</span>
                        </div>
                      </div>
                      <div className="status-badges">
                        <div className={`status-badge ${status.class}`}>
                          {status.icon} {status.text}
                        </div>
                        {entry.reviewHistory?.some(h => !h.reason || h.reason === 'reattempt' || h.reason === 'hod_reattempt') && (
                          <div className="status-badge status-reattempted">
                            <FaHistory /> Reattempted
                          </div>
                        )}
                        {entry.reviewHistory?.some(h => h.reason === 'reverified') && (
                          <div className="status-badge status-multi-reviewer">
                            <FaUserFriends /> Multiple Reviewers
                          </div>
                        )}
                        {entry.reattemptRequest && (
                          <div className="status-badge status-hod-reattempt">
                            <FaRedo /> Reattempt Requested
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="logbook-body">
                      <div className="logbook-info">
                        <div className="info-item">
                          <span className="info-label">Module:</span>
                          <span className="info-value">{getModuleName(entry.moduleId)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Type:</span>
                          <span className="info-value">{entry.entryType}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Submitted:</span>
                          <span className="info-value">{formatDate(entry.createdAt)}</span>
                        </div>
                        {entry.review?.reviewerName && (
                          <div className="info-item">
                            <span className="info-label">Verified by:</span>
                            <span className="info-value">{entry.review.reviewerName}</span>
                          </div>
                        )}
                      </div>

                      {isExpanded && (
                        <div className="expanded-content">
                          {renderEntryData(entry, modules)}

                          {entry.studentNotes && (
                            <div className="student-notes-preview">
                              <h5>Student Notes</h5>
                              <p>{entry.studentNotes}</p>
                            </div>
                          )}

                          {entry.review?.feedback && (
                            <div className="feedback-preview">
                              <h5>Verification Remarks</h5>
                              <p>{entry.review.feedback}</p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="logbook-actions">
                        <button className="action-btn expand-btn" onClick={() => toggleCard(entry._id)}>
                          {isExpanded ? <><FaChevronDown /> Hide Details</> : <><FaChevronRight /> Show Details</>}
                        </button>
                        <button className="action-btn view-btn" onClick={() => setDetailsEntryId(entry._id)}>
                          <FaEye /> Full View
                        </button>
                        {eligible && (
                          <button className="action-btn reattempt-request-btn" onClick={() => setReattemptTarget(entry)}>
                            <FaRedo /> Re-attempt
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {reattemptTarget && (
        <ReattemptRequestModal
          entry={reattemptTarget}
          moduleName={getModuleName(reattemptTarget.moduleId)}
          onClose={() => setReattemptTarget(null)}
          onSuccess={(updated) => {
            setEntries(prev => prev.map(e => e._id === updated._id ? updated : e));
            setSuccess('Reattempt requested successfully');
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {detailsEntryId && (
        <EntryDetailsModal
          entryId={detailsEntryId}
          onClose={() => setDetailsEntryId(null)}
        />
      )}
    </div>
  );
}
